import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Table, Modal } from '../components/Common';

export default function SowTemplates() {
  const { apiFetch, hasGroup } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  const canManage = hasGroup('MSP Billing Admins') || hasGroup('Super Admins');

  const load = useCallback(async () => {
    const res = await apiFetch('/sow-templates');
    if (res.ok) setTemplates(await res.json());
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    setCreating(true);
    try {
      const res = await apiFetch('/sow-templates', {
        method: 'POST',
        body: JSON.stringify({ name: 'New SoW Template', description: '' })
      });
      const data = await res.json();
      if (data.id) navigate(`/sow-templates/${data.id}/edit`);
    } catch (err) {
      console.error('Create SoW template error:', err);
    } finally {
      setCreating(false);
    }
  };

  const cloneTemplate = async (id) => {
    try {
      const res = await apiFetch(`/sow-templates/${id}/clone`, { method: 'POST' });
      const data = await res.json();
      if (data.id) navigate(`/sow-templates/${data.id}/edit`);
    } catch (err) {
      console.error('Clone template error:', err);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this SoW template?')) return;
    await apiFetch(`/sow-templates/${id}`, { method: 'DELETE' });
    load();
  };

  const toggleDefault = async (id, isDefault) => {
    await apiFetch(`/sow-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ isDefault: !isDefault })
    });
    load();
  };

  const exportTemplate = async (id, name) => {
    try {
      const res = await apiFetch(`/sow-templates/${id}/export`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(name || 'sow-template').replace(/[^a-zA-Z0-9_-]/g, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export template');
    }
  };

  const importTemplate = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const token = sessionStorage.getItem('token');
      const res = await fetch('/api/sow-templates/import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setImportModal(false);
        setImportFile(null);
        navigate(`/sow-templates/${data.id}/edit`);
      } else {
        setImportError(data.error || 'Import failed');
      }
    } catch (err) {
      setImportError('Failed to import template');
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    { key: 'name', label: 'Name', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {row.is_default && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', background: 'var(--primary)', color: '#fff', borderRadius: 8 }}>DEFAULT</span>
        )}
      </div>
    )},
    { key: 'description', label: 'Description', render: v => v ? (v.length > 60 ? v.substring(0, 60) + '...' : v) : '-' },
    { key: 'template_json', label: 'Sections', render: v => {
      const count = v?.sections?.length || 0;
      return <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{count} section{count !== 1 ? 's' : ''}</span>;
    }},
    { key: 'updated_at', label: 'Updated', render: v => v ? new Date(v).toLocaleDateString() : '-' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {canManage && (
          <Button variant="primary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); navigate(`/sow-templates/${row.id}/edit`); }}>
            Edit
          </Button>
        )}
        {canManage && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); cloneTemplate(row.id); }}>
            Clone
          </Button>
        )}
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); exportTemplate(row.id, row.name); }}>
          Export
        </Button>
        {canManage && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); toggleDefault(row.id, row.is_default); }}>
            {row.is_default ? 'Unset Default' : 'Set Default'}
          </Button>
        )}
        {canManage && !row.is_default && (
          <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); deleteTemplate(row.id); }}>
            Delete
          </Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>SoW Templates</h1>
        {canManage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => setImportModal(true)}>Import</Button>
            <Button onClick={createNew} disabled={creating}>
              {creating ? 'Creating...' : '+ New Template'}
            </Button>
          </div>
        )}
      </div>

      <Card>
        <Table columns={columns} data={templates} />
      </Card>

      <Modal isOpen={importModal} onClose={() => !importing && setImportModal(false)} title="Import SoW Template" width={480}>
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Select a previously exported SoW template JSON file to import.
        </div>
        <input type="file" accept=".json"
          onChange={(e) => { setImportFile(e.target.files[0] || null); setImportError(null); }}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', background: 'var(--bg-input)',
            color: 'var(--text)', fontSize: 14, marginBottom: 16, boxSizing: 'border-box'
          }}
        />
        {importError && (
          <div style={{ padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius)', background: '#7f1d1d', color: '#fca5a5', fontSize: 13 }}>
            {importError}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => { setImportModal(false); setImportFile(null); setImportError(null); }} disabled={importing}>Cancel</Button>
          <Button onClick={importTemplate} disabled={importing || !importFile}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
